// camera.js
import * as Three from 'three';
import { updateCameraPreview } from '../../pages/Dashboard/GridPage'; // Import from GridPage to update renderer
import {
  applyViewportCameraDefaults,
  perspectiveToOrthographicZoom,
  syncViewportCameraSwitch,
  updateOrthographicFrustum,
  VIEWPORT_CAMERA_FOV,
  VIEWPORT_CAMERA_NEAR,
  VIEWPORT_CAMERA_FAR,
} from '../../utils/ThreeJsUtils/viewportCameraSync';
import { VIEWPORT_CAMERA_NAMES } from '../../utils/ThreeJsUtils/sceneCameraUtils';

export const DEFAULT_CAMERA_NEAR = 0.1;
export const DEFAULT_CAMERA_FAR = 10;
export const DEFAULT_CAMERA_FOV = 50;

/** Factory / API values that should display as editor defaults until the user edits projection. */
export const LEGACY_SCENE_CAMERA_FAR = new Set([100, 200, 500, 1000, 2000, 5000]);
export const LEGACY_SCENE_CAMERA_FOV = new Set([60, 75]);

export function isLegacySceneCameraFar(far) {
  return far != null && Number.isFinite(far) && LEGACY_SCENE_CAMERA_FAR.has(far);
}

export function isLegacySceneCameraFov(fov) {
  return fov != null && Number.isFinite(fov) && LEGACY_SCENE_CAMERA_FOV.has(fov);
}

/** True when Redux has user-edited projection (not factory/API placeholders). */
export function hasPersistedSceneCameraProjection(camProp) {
  if (!camProp) return false;
  if (camProp.projectionEdited === true) return true;

  const nearPersisted =
    camProp.near != null && Number.isFinite(camProp.near);
  const farPersisted =
    camProp.far != null &&
    Number.isFinite(camProp.far) &&
    !isLegacySceneCameraFar(camProp.far);
  const fovPersisted =
    camProp.fov != null &&
    Number.isFinite(camProp.fov) &&
    !isLegacySceneCameraFov(camProp.fov);

  if (camProp.type === 'Orthographic') {
    return nearPersisted && farPersisted;
  }
  return nearPersisted && farPersisted && fovPersisted;
}

/** Panel + scene defaults when a camera has no saved projection in Redux yet. */
export function getSceneCameraProjectionDefaults(camProp) {
  if (!hasPersistedSceneCameraProjection(camProp)) {
    return {
      near: DEFAULT_CAMERA_NEAR,
      far: DEFAULT_CAMERA_FAR,
      fov: DEFAULT_CAMERA_FOV,
    };
  }
  return {
    near:
      camProp?.near != null && Number.isFinite(camProp.near)
        ? camProp.near
        : DEFAULT_CAMERA_NEAR,
    far:
      camProp?.far != null && Number.isFinite(camProp.far)
        ? camProp.far
        : DEFAULT_CAMERA_FAR,
    fov:
      camProp?.fov != null && Number.isFinite(camProp.fov)
        ? camProp.fov
        : DEFAULT_CAMERA_FOV,
  };
}

export function resolveSceneCameraFar(_threeFar, reduxFar, camProp) {
  const source =
    camProp ?? (reduxFar != null ? { far: reduxFar } : null);
  return getSceneCameraProjectionDefaults(source).far;
}

export function resolveSceneCameraFov(_threeFov, reduxFov, camProp) {
  const source =
    camProp ?? (reduxFov != null ? { fov: reduxFov } : null);
  return getSceneCameraProjectionDefaults(source).fov;
}

// Function to initialize cameras
export function initializeCameras(scene, renderer, controlsOrbit) {
  const aspect = window.innerWidth / window.innerHeight || 1;
  // Perspective Camera
  const perspectiveCamera = new Three.PerspectiveCamera(
    VIEWPORT_CAMERA_FOV,
    aspect,
    VIEWPORT_CAMERA_NEAR,
    VIEWPORT_CAMERA_FAR
  );
  perspectiveCamera.position.set(0, 2.5, 12);
  perspectiveCamera.name = 'perspectiveCamera';

  const orthographicCamera = new Three.OrthographicCamera(
    window.innerWidth / -2,
    window.innerWidth / 2,
    window.innerHeight / 2,
    window.innerHeight / -2,
    VIEWPORT_CAMERA_NEAR,
    VIEWPORT_CAMERA_FAR
  );
  orthographicCamera.name = 'orthographicCamera';
  applyViewportCameraDefaults(perspectiveCamera, orthographicCamera);
  orthographicCamera.position.copy(perspectiveCamera.position);
  orthographicCamera.quaternion.copy(perspectiveCamera.quaternion);
  orthographicCamera.zoom = perspectiveToOrthographicZoom(
    perspectiveCamera,
    new Three.Vector3(0, 0, 0),
    window.innerWidth,
    window.innerHeight
  );
  orthographicCamera.updateProjectionMatrix();

  // Set initial camera
  let currentCamera = perspectiveCamera;

  // Update controls to use the current camera
  if (controlsOrbit) {
    controlsOrbit.object = currentCamera;
    controlsOrbit.update();
  }

  return { perspectiveCamera, orthographicCamera, currentCamera };
}

// Function to switch camera type
export function switchCamera(
  type,
  perspectiveCamera,
  orthographicCamera,
  currentCamera,
  renderer,
  scene, // <-- add scene here
  controlsOrbit,
  updateCallback
) {
  let newCamera = currentCamera;

  if (type === 'Perspective') {
    newCamera = perspectiveCamera;
  } else if (type === 'Orthographic') {
    newCamera = orthographicCamera;
  }

  if (newCamera !== currentCamera) {
    const orbitTarget = controlsOrbit?.target;
    syncViewportCameraSwitch(
      currentCamera,
      newCamera,
      orbitTarget,
      window.innerWidth,
      window.innerHeight
    );
    if (controlsOrbit && typeof controlsOrbit.update === 'function') {
      controlsOrbit.object = newCamera;
      controlsOrbit.update();
    } else if (controlsOrbit) {
      controlsOrbit.object = newCamera;
    }

    // Update global camera references
    window.currentCamera = newCamera;
    window.camera = newCamera;
    if (typeof updateCallback === 'function') {
      updateCallback(newCamera);
    }

    // Update renderer
    updateCameraPreview({
      width: window.innerWidth,
      height: window.innerHeight,
    });

    if (renderer && typeof renderer.render === 'function') {
      renderer.render(scene, newCamera);
    }
  }

  return newCamera;
}

/** True when editing the grid viewport cameras (not a scene-placed camera). */
export function isViewportCamera(camera, perspectiveCamera, orthographicCamera) {
  return (
    camera === perspectiveCamera || camera === orthographicCamera
  );
}

/** Stable scene id for a placed camera (matches Three.js object.name). */
export function getCameraLookupId(threeCamera) {
  if (!threeCamera) return null;
  return threeCamera.userData?.id || threeCamera.name || null;
}

export function getCameraPropId(camProp) {
  return camProp?._id || camProp?.id || null;
}

/** Read position/rotation from a Three.js camera for Redux persistence. */
export function getCameraTransformVectors(threeCamera) {
  if (!threeCamera) return null;
  return {
    position: {
      x: threeCamera.position.x,
      y: threeCamera.position.y,
      z: threeCamera.position.z,
    },
    rotation: {
      x: threeCamera.rotation.x,
      y: threeCamera.rotation.y,
      z: threeCamera.rotation.z,
    },
  };
}

function isNamedViewportCamera(obj) {
  return !!(obj && VIEWPORT_CAMERA_NAMES.has(obj.name));
}

/** Snapshot scene camera asset transforms (never mutate these during preview look-through). */
export function snapshotSceneCameraTransforms(scene) {
  const snapshots = new Map();
  if (!scene) return snapshots;
  scene.traverse((obj) => {
    if (!obj?.isCamera || isNamedViewportCamera(obj)) return;
    snapshots.set(obj.uuid, {
      position: obj.position.clone(),
      quaternion: obj.quaternion.clone(),
      rotation: obj.rotation.clone(),
      scale: obj.scale.clone(),
    });
  });
  return snapshots;
}

/** Restore scene camera assets from a snapshot map (uuid → transform). */
export function restoreSceneCameraTransforms(scene, snapshots) {
  if (!scene || !snapshots?.size) return;
  scene.traverse((obj) => {
    if (!obj?.isCamera || isNamedViewportCamera(obj)) return;
    const snap = snapshots.get(obj.uuid);
    if (!snap) return;
    obj.position.copy(snap.position);
    obj.scale.copy(snap.scale);
    // Prefer stored Euler when present so the inspector matches design values.
    if (snap.rotation) {
      obj.rotation.copy(snap.rotation);
    } else {
      obj.quaternion.copy(snap.quaternion);
      obj.rotation.setFromQuaternion(snap.quaternion, obj.rotation.order);
    }
    obj.updateMatrix?.();
    obj.updateMatrixWorld?.(true);
  });
}

/**
 * Re-apply authored transforms from Redux camera_properties onto live scene cameras.
 * Use after preview → design so look-through / orbit never sticks on assets.
 */
export function syncSceneCamerasFromReduxProperties(scene, cameraProperties = []) {
  if (!scene || !cameraProperties?.length) return;
  cameraProperties.forEach((camProp) => {
    const id = getCameraPropId(camProp);
    const cam =
      (id && scene.getObjectByName(id)) ||
      (camProp?.name && scene.getObjectByName(camProp.name)) ||
      null;
    if (!cam?.isCamera || isNamedViewportCamera(cam)) return;

    if (camProp.position && typeof camProp.position.x === 'number') {
      cam.position.set(
        camProp.position.x ?? 0,
        camProp.position.y ?? 0,
        camProp.position.z ?? 0
      );
    }
    if (camProp.rotation && typeof camProp.rotation.x === 'number') {
      cam.rotation.set(
        camProp.rotation.x ?? 0,
        camProp.rotation.y ?? 0,
        camProp.rotation.z ?? 0
      );
    }
    cam.updateMatrix?.();
    cam.updateMatrixWorld?.(true);

    const pointName = `${cam.name}Point`;
    const point = scene.getObjectByName(pointName);
    if (point) point.position.copy(cam.position);
  });
}

/**
 * If the scene camera is still at origin / identity but Redux has a saved
 * transform, apply it. Never overwrite an already-authored live transform —
 * that was resetting rotation after preview → design when Redux lagged the gizmo.
 */
export function applyCameraTransformFromProps(threeCamera, camProp) {
  if (!threeCamera || !camProp) return false;
  let applied = false;
  const p = camProp.position;
  if (p && typeof p.x === 'number') {
    const atOrigin =
      threeCamera.position.x === 0 &&
      threeCamera.position.y === 0 &&
      threeCamera.position.z === 0;
    const hasSaved = p.x !== 0 || p.y !== 0 || p.z !== 0;
    if (atOrigin && hasSaved) {
      threeCamera.position.set(p.x, p.y ?? 0, p.z ?? 0);
      applied = true;
    }
  }
  const r = camProp.rotation;
  if (r && typeof r.x === 'number') {
    const atIdentity =
      Math.abs(threeCamera.rotation.x) < 1e-6 &&
      Math.abs(threeCamera.rotation.y) < 1e-6 &&
      Math.abs(threeCamera.rotation.z) < 1e-6;
    const hasSaved =
      Math.abs(r.x) > 1e-6 ||
      Math.abs(r.y ?? 0) > 1e-6 ||
      Math.abs(r.z ?? 0) > 1e-6;
    if (atIdentity && hasSaved) {
      threeCamera.rotation.set(r.x, r.y ?? 0, r.z ?? 0);
      applied = true;
    }
  }
  return applied;
}

/** Match Redux camera_properties by scene id only (_id / id), not display name. */
export function findCameraProp(camera_properties, lookupId) {
  if (!lookupId || !camera_properties?.length) return null;
  return (
    camera_properties.find(
      (c) => c._id === lookupId || c.id === lookupId
    ) ?? null
  );
}

/**
 * Recreate CameraHelper after projection changes (perspective ↔ orthographic).
 * CameraHelper must be bound to the camera instance; .update() alone is not enough.
 */
export function refreshCameraHelper(scene, camera) {
  if (!scene || !camera) return null;

  const cameraName = camera.name || camera.userData?.id || 'DemoCamera';
  const helperName = `${cameraName}Helper`;

  const existing = scene.getObjectByName(helperName);
  if (existing) {
    if (existing.parent) existing.parent.remove(existing);
    if (typeof existing.dispose === 'function') existing.dispose();
  }

  const helper = new Three.CameraHelper(camera);
  helper.name = helperName;
  helper.scale.set(0.5, 0.5, 0.5);
  helper.castShadow = false;
  helper.receiveShadow = false;

  const tintRed = (mat) => {
    if (mat?.color) mat.color.set('red');
  };
  if (Array.isArray(helper.material)) {
    helper.material.forEach(tintRed);
  } else {
    tintRed(helper.material);
  }

  scene.add(helper);
  helper.update();

  const point = scene.getObjectByName(`${cameraName}Point`);
  if (point) {
    point.position.copy(camera.position);
  }

  return helper;
}

/** Update an existing CameraHelper frustum; recreate only if missing or bound to another camera. */
export function updateCameraHelperFrustum(scene, camera) {
  if (!scene || !camera) return null;

  const cameraName = camera.name || camera.userData?.id || 'DemoCamera';
  const helperName = `${cameraName}Helper`;
  const helper = scene.getObjectByName(helperName);

  if (helper?.camera === camera && typeof helper.update === 'function') {
    helper.update();
    const point = scene.getObjectByName(`${cameraName}Point`);
    if (point) point.position.copy(camera.position);
    return helper;
  }

  return refreshCameraHelper(scene, camera);
}

/**
 * Change projection type on a scene camera without swapping the viewport camera.
 */
export function replaceSceneCameraType(
  sceneCamera,
  newType,
  scene,
  {
    near,
    far,
    fov = 50,
    orthoSize = 5,
    aspect = window.innerWidth / window.innerHeight || 1,
  } = {}
) {
  if (!sceneCamera || !scene) return sceneCamera;

  const wantOrtho = newType === 'Orthographic';
  const isOrtho = sceneCamera.isOrthographicCamera;
  const isPersp = sceneCamera.isPerspectiveCamera;
  if ((wantOrtho && isOrtho) || (!wantOrtho && isPersp)) {
    return sceneCamera;
  }

  const name = sceneCamera.name;
  const parent = sceneCamera.parent;
  const position = sceneCamera.position.clone();
  const rotation = sceneCamera.rotation.clone();
  const quaternion = sceneCamera.quaternion.clone();
  const scale = sceneCamera.scale.clone();
  const userData = { ...sceneCamera.userData };

  const resolvedNear =
    typeof near === 'number' ? near : sceneCamera.near ?? 0.1;
  const resolvedFar =
    typeof far === 'number' ? far : sceneCamera.far ?? DEFAULT_CAMERA_FAR;

  let newCamera;
  if (wantOrtho) {
    const size = Number(orthoSize) || 5;
    newCamera = new Three.OrthographicCamera(
      -(size * aspect) / 2,
      (size * aspect) / 2,
      size / 2,
      -size / 2,
      resolvedNear,
      resolvedFar
    );
  } else {
    newCamera = new Three.PerspectiveCamera(
      typeof fov === 'number' ? fov : sceneCamera.fov ?? 50,
      aspect,
      resolvedNear,
      resolvedFar
    );
  }

  newCamera.name = name;
  newCamera.position.copy(position);
  newCamera.rotation.copy(rotation);
  newCamera.quaternion.copy(quaternion);
  newCamera.scale.copy(scale);
  newCamera.userData = userData;
  newCamera.updateProjectionMatrix();

  if (parent) {
    const index = parent.children.indexOf(sceneCamera);
    parent.remove(sceneCamera);
    if (index >= 0) {
      parent.children.splice(index, 0, newCamera);
      newCamera.parent = parent;
    } else {
      parent.add(newCamera);
    }
  } else {
    scene.remove(sceneCamera);
    scene.add(newCamera);
  }

  refreshCameraHelper(scene, newCamera);
  return newCamera;
}

// Function to update camera properties
export function updateCameraProperties(
  camera,
  property,
  value,
  renderer,
  scene
) {
  if (!camera) return;

  switch (property) {
    case 'near':
      camera.near = value;
      break;
    case 'far':
      camera.far = value;
      break;
    case 'fov':
      if (camera instanceof Three.PerspectiveCamera) {
        camera.fov = value;
      }
      break;
    case 'zoom':
      camera.zoom = value;
      break;
    default:
      break;
  }

  camera.updateProjectionMatrix();
  renderer?.render(scene, camera);
}

/** Reset viewport (editor) camera projection, position, and orbit target. */
export function resetViewportCamera(camera, type, renderer, scene, controlsOrbit) {
  const aspect = window.innerWidth / window.innerHeight;

  if (type === 'Perspective') {
    camera.fov = VIEWPORT_CAMERA_FOV;
    camera.near = VIEWPORT_CAMERA_NEAR;
    camera.far = VIEWPORT_CAMERA_FAR;
    camera.position.set(0, 2.5, -12);
  } else if (type === 'Orthographic') {
    camera.left = window.innerWidth / -2;
    camera.right = window.innerWidth / 2;
    camera.top = window.innerHeight / 2;
    camera.bottom = window.innerHeight / -2;
    camera.near = VIEWPORT_CAMERA_NEAR;
    camera.far = VIEWPORT_CAMERA_FAR;
    camera.zoom = 50;
    camera.position.set(0, 2.5, -12);
  }

  camera.updateProjectionMatrix();
  controlsOrbit.target.set(0, 0, 0);
  controlsOrbit.update();
  if (renderer && typeof renderer.render === 'function') {
    renderer.render(scene, camera);
  }
}

/** @deprecated Use resetViewportCamera — scene cameras must not call this. */
export const resetCamera = resetViewportCamera;

export default {
  initializeCameras,
  switchCamera,
  replaceSceneCameraType,
  isViewportCamera,
  refreshCameraHelper,
  updateCameraProperties,
  resetViewportCamera,
  resetCamera,
  snapshotSceneCameraTransforms,
  restoreSceneCameraTransforms,
  syncSceneCamerasFromReduxProperties,
};
